import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, IconButton, Tooltip, Button } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useNavigate } from 'react-router-dom';
import octopusInstance from '../../../services/octopus/octopusInstance';
import { useDispatch, useSelector } from 'react-redux';
import { openOrderWindow } from '../../../store/actions/orders';
import { getEffectiveOrderPayload } from '../../../services/utilities/orderUtils';
const FutureContractRow = ({ 
  title, 
  symbol, 
  expiry, 
  price: propPrice, 
  change: propChange, 
  pct: propPct, 
  token, 
  wsCode = 2, // Default to 2 (NFO) as requested for F&O
  exchange,
  price_precision,
  onPriceUpdate,
  instrument
}) => {
  const [wsData, setWsData] = useState({ price: propPrice || 0, change: propChange || 0, pct: propPct || 0 });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userSettings } = useSelector((state) => state.profile);
  const positionsArray = useSelector((state) => state.portfolio?.arrPositionsUpdateData || []);

  const handleSymbolClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (instrument) {
      const currentLtp = token ? wsData.price : (propPrice || 0);
      const instrumentWithLtp = { ...instrument, ltp: currentLtp || instrument.ltp };
      const payload = getEffectiveOrderPayload(instrumentWithLtp, 'BUY', userSettings, positionsArray);
      dispatch(openOrderWindow(payload));
    }
  };

  const handleOptionChainClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (exchange && token) {
      const precision = price_precision ?? 0;
      navigate(`/overview/tdv/${exchange}/${token}/${precision}`, {
        state: { defaultTab: 'chart' }
      });
    }
  };

  useEffect(() => {
    if (!token) return;

    const handler = octopusInstance.wsHandler({
      messageType: 'DetailMarketDataMessage',
      subscriptionLocation: `future-row-${token}`,
      payload: { exchangeCode: wsCode, instrumentToken: Number(token) },
    });

    handler.subscribe(({ msg }) => {
      const price = msg.lastTradedPrice ?? msg.ltp;
      if (price) {
        const newData = {
          price: price,
          change: msg.absoluteChange ?? 0,
          pct: msg.percentChange ?? 0
        };
        setWsData(newData);
        if (onPriceUpdate) onPriceUpdate(newData);
      }
    });

    return () => handler.unsubscribe();
  }, [token, wsCode]);

  const ltp = token ? wsData.price : (propPrice || 0);
  const absChange = token ? wsData.change : (propChange || 0);
  const pctChange = token ? wsData.pct : (propPct || 0);
  
  const ltpValue = parseFloat(ltp);
  const isPositive = absChange >= 0;
  const textColor = isPositive ? 'success.main' : 'error.main';

  return (
    <Box sx={{ mb: 2 }}>
      {title && (
        <Typography variant="subtitle2" fontWeight="700" mb={1}>
          {title}
        </Typography>
      )}
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 1.5, lg: 2 }, 
          backgroundColor: 'background.paper', 
          border: '1px solid #E0E0E0', 
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: { xs: 1, sm: 1.5, lg: 3 },
          width: '100%',
          overflow: 'hidden',
          boxShadow: '0 2px 4px rgba(0,0,0,0.02)' // Subtle shadow for premium feel
        }}
      >
        {/* LEFT: Symbol & Expiry — natural width, no grow */}
        <Box sx={{ flex: '0 1 auto', display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, alignItems: { xs: 'flex-start', lg: 'center' }, gap: { xs: 0, lg: 2 }, minWidth: 0, overflow: 'hidden' }}>
          <Box 
            onClick={handleSymbolClick}
            sx={{ 
              cursor: instrument ? 'pointer' : 'default',
              '&:hover': instrument ? { color: 'primary.main' } : {},
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Typography 
              variant="subtitle1" 
              fontWeight="700" 
              noWrap 
              sx={{ 
                fontSize: { xs: '0.8rem', sm: '0.9rem', lg: '1rem' },
                transition: 'color 0.2s',
                color: 'inherit'
              }}
            >
              {symbol}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" fontWeight="600" sx={{ fontSize: { xs: '0.65rem', lg: '0.75rem' } }}>
            {expiry}
          </Typography>
        </Box>

        {/* CENTER: Chart button spacer — flex:1 keeps icon centered between name and price on all screen sizes */}
        <Box sx={{ flex: '1 1 0', display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: 0 }}>
          {exchange && token && (
            <>
              {/* Mobile/Tablet: Icon Only */}
              <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
                <Tooltip title="Option Chain + Charts">
                  <IconButton 
                    onClick={handleOptionChainClick}
                    size="small"
                    color="primary"
                    sx={{ 
                      bgcolor: 'action.hover',
                      '&:hover': { bgcolor: 'action.selected' }
                    }}
                  >
                    <TimelineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Desktop: Icon + Text */}
              <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
                <Button
                  onClick={handleOptionChainClick}
                  size="small"
                  color="primary"
                  variant="outlined"
                  startIcon={<TimelineIcon />}
                  sx={{ 
                    textTransform: 'none', 
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    borderRadius: '16px',
                    px: 2,
                    bgcolor: 'action.hover',
                    '&:hover': { bgcolor: 'action.selected' },
                    borderColor: 'divider'
                  }}
                >
                  Option Chain + Charts
                </Button>
              </Box>
            </>
          )}
        </Box>

        {/* RIGHT: Price & Change — natural width, no grow, right-aligned */}
        <Box sx={{ flex: '0 0 auto', display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, alignItems: { xs: 'flex-end', lg: 'center' }, gap: { xs: 0.2, lg: 2 } }}>
          <Typography 
            variant="subtitle1" 
            fontWeight="700" 
            noWrap
            color={isPositive ? 'success.main' : 'error.main'}
            sx={{ fontSize: { xs: '0.9rem', sm: '1rem', lg: '1.1rem' }, lineHeight: 1.2, whiteSpace: 'nowrap' }}
          >
            ₹{ltpValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isPositive ? (
              <ArrowDropUpIcon sx={{ color: 'success.main', fontSize: 16, ml: -0.5 }} />
            ) : (
              <ArrowDropDownIcon sx={{ color: 'error.main', fontSize: 16, ml: -0.5 }} />
            )}
            <Typography 
              variant="caption" 
              fontWeight="600"
              noWrap
              color={isPositive ? 'success.main' : 'error.main'}
              sx={{ fontSize: { xs: '0.65rem', lg: '0.75rem' }, whiteSpace: 'nowrap' }}
            >
              {isPositive ? '+' : ''}{Math.abs(absChange).toFixed(2)} ({isPositive ? '+' : ''}{pctChange.toFixed(2)}%)
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default FutureContractRow;
